// ET animation groups (.anim with #include'd .aninc): which frames of which
// MDX file make up each named animation. Port of BG_R_RegisterAnimationGroup /
// BG_RAG_ParseAnimation (bg_animgroup.c).

import type { VirtualFS } from '../../fs/vfs';
import { tokenize } from '../../shared/tokenize';

export const ANIMFL_LADDERANIM = 0x1;
export const ANIMFL_FIRINGANIM = 0x2;
export const ANIMFL_REVERSED = 0x4;

export interface Animation {
  /** Lower case. */
  name: string;
  /** MDX file the frames are in. */
  mdxFile: string;
  firstFrame: number;
  numFrames: number;
  loopFrames: number;
  /** ms between frames (int, 1000 / fps). */
  frameLerp: number;
  /** ms to get to the first frame. */
  initialLerp: number;
  /** Speed the animation was made for (units/s); -1 in the file = measured from the feet (CG_CalcMoveSpeeds). */
  moveSpeed: number;
  /** Blend time to the next animation. */
  animBlend: number;
  duration: number;
  flags: number;
  /** Bit mask of the movetypes whose script commands play this animation on the legs (set by the script parser). */
  movetype: number;
}

/** Replaces #include "file" lines with the file contents (recursively), like the botlib precompiler. */
export async function expandIncludes(vfs: VirtualFS, path: string, depth = 0): Promise<string> {
  const text = await vfs.readText(path);
  if (text === null) throw new Error(`${path} not found`);
  if (depth > 8) throw new Error(`${path}: #include nested too deep`);
  const lines = text.split(/\r?\n/);
  const out: string[] = [];
  for (const line of lines) {
    const m = /^\s*#include\s+["<]([^">]+)[">]/.exec(line);
    out.push(m ? await expandIncludes(vfs, m[1], depth + 1) : line);
  }
  return out.join('\n');
}

const int = (s: string | undefined, what: string): number => {
  const v = Number.parseInt(s ?? '', 10);
  if (Number.isNaN(v)) throw new Error(`animation group: expected ${what}, found "${s}"`);
  return v;
};

/** Parses an animation group (includes already expanded). */
export function parseAnimGroup(text: string): Animation[] {
  const tokens = tokenize(text).map((t) => t.text);
  let i = 0;
  const next = () => tokens[i++];
  if (next()?.toLowerCase() !== 'animgroup') throw new Error("animation group: expected 'animgroup'");
  if (next() !== '{') throw new Error("animation group: expected '{'");
  const anims: Animation[] = [];
  for (;;) {
    const t = next();
    if (t === undefined || t === '}') break;
    if (t.toLowerCase() !== 'animfile') throw new Error(`animation group: unknown token '${t}'`);
    const mdxFile = next();
    if (!mdxFile) throw new Error('animation group: expected mdx filename');
    if (next() !== '{') throw new Error("animation group: expected '{'");
    for (;;) {
      const name = next();
      if (name === undefined) throw new Error('animation group: unexpected EOF');
      if (name === '}') break;
      const firstFrame = int(next(), 'first frame integer');
      const numFrames = int(next(), 'length integer');
      const loopFrames = int(next(), 'looping integer');
      let fps = int(next(), 'fps integer');
      if (fps === 0) fps = 1;
      const moveSpeed = int(next(), 'move speed integer');
      const animBlend = int(next(), 'transition integer');
      const reversed = int(next(), 'reversed integer');
      const lname = name.toLowerCase();
      const frameLerp = Math.trunc(1000 / fps);
      let initialLerp = frameLerp;
      let flags = reversed === 1 ? ANIMFL_REVERSED : 0;
      const duration = initialLerp + frameLerp * numFrames + animBlend;
      if (lname.startsWith('climb')) flags |= ANIMFL_LADDERANIM;
      if (lname.includes('firing')) {
        flags |= ANIMFL_FIRINGANIM;
        initialLerp = 40;
      }
      anims.push({ name: lname, mdxFile: mdxFile.toLowerCase(), firstFrame, numFrames, loopFrames, frameLerp, initialLerp, moveSpeed, animBlend, duration, flags, movetype: 0 });
    }
  }
  return anims;
}

/** The MDX files an animation group uses. */
export function animGroupFiles(anims: Animation[]): string[] {
  return [...new Set(anims.map((a) => a.mdxFile))];
}
